import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc, updateDoc, addDoc, collection, arrayUnion, setDoc, getDoc, getDocs,
  query, where, collectionGroup,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from '../components/ImageUpload';
import { planForNewFamily, getUserPlan, getLimits } from '../utils/planLimits';
import { createFamilyCompletionSubcollection } from '../utils/familyCompletion';
import { generateUniqueFamilyCode, normalizeFamilyCode, displayFamilyCode } from '../utils/familyCode';
import { getAge, MIN_FAMILY_ADMIN_AGE } from '../utils/age';

// ── Step sequences ────────────────────────────────────────────────────────────

const STEPS_CREATE       = ['create', 'invite', 'profile', 'video'];
const STEPS_JOIN         = ['join', 'profile', 'video'];
const STEPS_INVITED      = ['profile', 'video'];
const STEPS_LINK_INVITE  = ['accept-invite', 'profile', 'video'];

const STEP_LABELS = {
  'accept-invite': 'Invitation',
  create:          'Family',
  invite:          'Invite',
  join:            'Find Family',
  profile:         'Profile',
  video:           'Welcome',
};

// ── Progress Bar ──────────────────────────────────────────────────────────────

function ProgressBar({ currentStep, steps }) {
  const idx = steps.indexOf(currentStep);
  return (
    <div className="flex items-end justify-center gap-1 mb-8">
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
              i < idx  ? 'bg-cerulean text-white' :
              i === idx ? 'bg-cerulean text-white ring-4 ring-cerulean/20' :
                          'bg-gray-200 text-gray-500'
            }`}>
              {i < idx ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">{STEP_LABELS[step]}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 w-8 mb-4 transition-all ${i < idx ? 'bg-cerulean' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-cerulean' : 'bg-gray-300'}`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

// ── Step 1a: Create Family ────────────────────────────────────────────────────

function CreateFamilyStep({ onNext, onSwitchToJoin }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!familyName.trim()) { setError('Family name is required'); return; }
    const age = getAge(currentUser?.birthday);
    if (age === null || age < MIN_FAMILY_ADMIN_AGE) {
      setError(`You must be at least ${MIN_FAMILY_ADMIN_AGE} to create and administer a family.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const familyCode = await generateUniqueFamilyCode(familyName.trim());
      const familyData = {
        name: familyName.trim(),
        familyCode,
        bio: '',
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        memberIDs: [currentUser.uid],
        updatedAt: new Date().toISOString(),
        plan: planForNewFamily(currentUser),
      };
      const docRef = await addDoc(collection(db, 'families'), familyData);
      await updateDoc(doc(db, 'users', currentUser.uid), { familyIds: arrayUnion(docRef.id) });
      await setDoc(doc(db, 'families', docRef.id, 'members', currentUser.uid), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '',
        bio: '',
        joinedAt: new Date().toISOString(),
        role: 'admin',
        canRecord: true,
        canCommentLike: true,
        canAddNotes: true,
        canInvite: true,
      });
      try { await createFamilyCompletionSubcollection(docRef.id, db); } catch (_) {}
      onNext(docRef.id, familyName.trim());
    } catch (err) {
      console.error('Error creating family:', err);
      setError('Failed to create family. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-14 h-14 bg-cerulean/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <svg className="w-7 h-7 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-space-cadet">Create Your Family</h2>
        <p className="text-gray-500 mt-1 text-sm">Give your family group a name to get started.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-space-cadet mb-2">Family Name</label>
        <input
          type="text"
          value={familyName}
          onChange={e => setFamilyName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          placeholder="e.g. The Johnson Family"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors"
          autoFocus
        />
      </div>

      <button
        onClick={handleCreate}
        disabled={loading}
        className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Creating Family...' : 'Create Family & Continue'}
      </button>

      <button
        onClick={onSwitchToJoin}
        className="w-full py-2 text-sm text-cerulean hover:text-cerulean/80 transition-colors"
      >
        Joining an existing family instead?
      </button>
    </div>
  );
}

// ── Step 1b: Join Family ──────────────────────────────────────────────────────

function JoinFamilyStep({ onNext }) {
  const { currentUser } = useAuth();
  const [phase, setPhase] = useState('checking'); // checking | invitations | enter-code | code-found | request-sent
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [familyCode, setFamilyCode] = useState('');
  const [foundFamily, setFoundFamily] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function checkInvitations() {
      try {
        const q = query(
          collectionGroup(db, 'invitations'),
          where('invitedUserEmail', '==', currentUser.email),
          where('status', '==', 'pending')
        );
        const snap = await getDocs(q);
        const invites = await Promise.all(snap.docs.map(async d => {
          const data = d.data();
          const familyId = d.ref.parent.parent.id;
          let familyName = data.familyName;
          if (!familyName) {
            try {
              const fSnap = await getDoc(doc(db, 'families', familyId));
              familyName = fSnap.data()?.name || 'a family';
            } catch { familyName = 'a family'; }
          }
          return { id: d.id, familyId, familyName, ...data };
        }));
        setPendingInvitations(invites);
        setPhase(invites.length > 0 ? 'invitations' : 'enter-code');
      } catch (err) {
        console.error('Error checking invitations:', err);
        setPhase('enter-code');
      }
    }
    checkInvitations();
  }, [currentUser]);

  const handleAccept = async (invitation) => {
    setAcceptingId(invitation.id);
    try {
      await updateDoc(doc(db, 'families', invitation.familyId), {
        memberIDs: arrayUnion(currentUser.uid),
        [`memberPermissions.${currentUser.uid}`]: { canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true },
      });
      await updateDoc(doc(db, 'users', currentUser.uid), { familyIds: arrayUnion(invitation.familyId) });
      await setDoc(doc(db, 'families', invitation.familyId, 'members', currentUser.uid), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '',
        bio: '',
        joinedAt: new Date().toISOString(),
        role: 'member',
        canRecord: true,
        canCommentLike: true,
        canAddNotes: true,
        canInvite: true,
      });
      await updateDoc(doc(db, 'families', invitation.familyId, 'invitations', invitation.id), {
        status: 'accepted',
        acceptedAt: new Date().toISOString(),
        acceptedBy: currentUser.uid,
      });
      onNext(invitation.familyName);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setAcceptingId(null);
    }
  };

  const handleLookupCode = async () => {
    const code = normalizeFamilyCode(familyCode);
    if (code.length !== 9) { setError('Please enter a valid 9-character family code.'); return; }
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(collection(db, 'families'), where('familyCode', '==', code)));
      if (snap.empty) {
        setError("We couldn't find a family with that code. Double-check it and try again.");
      } else {
        setFoundFamily({ id: snap.docs[0].id, ...snap.docs[0].data() });
        setPhase('code-found');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendJoinRequest = async () => {
    setLoading(true);
    setError('');
    try {
      await setDoc(doc(db, 'families', foundFamily.id, 'requests', currentUser.uid), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '',
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingFamilyId: foundFamily.id,
        pendingFamilyName: foundFamily.name,
      });
      setPhase('request-sent');
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'checking') {
    return (
      <div className="flex flex-col items-center py-12 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cerulean" />
        <p className="text-gray-500 text-sm">Checking for invitations…</p>
      </div>
    );
  }

  if (phase === 'invitations') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-space-cadet">
            {pendingInvitations.length === 1 ? "You've Been Invited!" : `${pendingInvitations.length} Invitations Found`}
          </h2>
          <p className="text-gray-500 mt-1 text-sm">Accept an invitation below to join your family.</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
        )}

        <div className="space-y-3">
          {pendingInvitations.map(inv => (
            <div key={inv.id} className="border-2 border-cerulean/20 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-space-cadet">{inv.familyName}</div>
                <div className="text-sm text-gray-500">Invited by {inv.invitedByName}</div>
              </div>
              <button
                onClick={() => handleAccept(inv)}
                disabled={!!acceptingId}
                className="bg-gradient-to-r from-cerulean to-aqua-deep text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex-shrink-0"
              >
                {acceptingId === inv.id ? 'Joining…' : 'Accept'}
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={() => setPhase('enter-code')}
          className="w-full py-2 text-sm text-cerulean hover:text-cerulean/80 transition-colors"
        >
          Use a family code instead
        </button>
        <button
          onClick={() => onNext()}
          className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Skip for now
        </button>
      </div>
    );
  }

  if (phase === 'enter-code') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 bg-cerulean/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-space-cadet">Find Your Family</h2>
          <p className="text-gray-500 mt-1 text-sm">
            {pendingInvitations.length === 0
              ? `No invitations found for ${currentUser.email}. Enter a family code if you have one.`
              : "Enter the 9-character family code your family admin shared with you."}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
        )}

        <div>
          <label className="block text-sm font-medium text-space-cadet mb-2">Family Code</label>
          <input
            type="text"
            value={familyCode}
            onChange={e => setFamilyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 9))}
            placeholder="e.g. SMITK7X2P"
            maxLength={9}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors text-center text-xl font-mono tracking-widest uppercase"
            autoFocus
            autoCapitalize="characters"
          />
          <p className="text-xs text-gray-400 mt-1 text-center">9-character code — ask your family admin (dash optional)</p>
        </div>

        <button
          onClick={handleLookupCode}
          disabled={loading || familyCode.length < 9}
          className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Searching…' : 'Find Family'}
        </button>

        {pendingInvitations.length > 0 && (
          <button
            onClick={() => setPhase('invitations')}
            className="w-full py-2 text-sm text-cerulean hover:text-cerulean/80 transition-colors"
          >
            ← Back to invitations
          </button>
        )}

        <button
          onClick={() => onNext()}
          className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Skip for now
        </button>
      </div>
    );
  }

  if (phase === 'code-found') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-space-cadet">Family Found!</h2>
          <p className="text-gray-500 mt-1 text-sm">Is this your family?</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
        )}

        <div className="border-2 border-cerulean/30 rounded-2xl p-6 text-center">
          <div className="w-14 h-14 bg-cerulean/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="text-xl font-bold text-space-cadet">{foundFamily.name}</div>
          <div className="text-sm text-gray-500 mt-1">Family code: {displayFamilyCode(foundFamily.familyCode)}</div>
        </div>

        <button
          onClick={handleSendJoinRequest}
          disabled={loading}
          className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending Request…' : 'Request to Join'}
        </button>

        <button
          onClick={() => { setPhase('enter-code'); setFoundFamily(null); setError(''); }}
          className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Try a different code
        </button>
      </div>
    );
  }

  if (phase === 'request-sent') {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-space-cadet">Request Sent!</h2>
          <p className="text-gray-500 mt-2 text-sm">
            Your request to join <strong>{foundFamily.name}</strong> has been sent to the family admin.
            You'll get access once they approve it.
          </p>
        </div>
        <button
          onClick={() => onNext()}
          className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg"
        >
          Continue Setting Up Your Profile
        </button>
      </div>
    );
  }

  return null;
}

// ── Step 1c: Accept Email Invitation (via link) ───────────────────────────────

function AcceptInviteStep({ familyId, invitationId, onNext }) {
  const { currentUser } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const invSnap = await getDoc(doc(db, 'families', familyId, 'invitations', invitationId));
        if (!invSnap.exists()) { setError('This invitation could not be found or has expired.'); setLoading(false); return; }
        const data = invSnap.data();
        if (data.status === 'accepted') { setError('This invitation has already been accepted.'); setLoading(false); return; }
        if (data.expiresAt && new Date() > new Date(data.expiresAt)) { setError('This invitation has expired.'); setLoading(false); return; }
        setInvitation({ id: invSnap.id, ...data });

        const famSnap = await getDoc(doc(db, 'families', familyId));
        setFamilyName(famSnap.data()?.name || 'your family');
      } catch (err) {
        setError('Failed to load invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [familyId, invitationId]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      await updateDoc(doc(db, 'families', familyId), {
        memberIDs: arrayUnion(currentUser.uid),
        [`memberPermissions.${currentUser.uid}`]: { canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true },
      });
      await updateDoc(doc(db, 'users', currentUser.uid), { familyIds: arrayUnion(familyId) });
      await setDoc(doc(db, 'families', familyId, 'members', currentUser.uid), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '',
        bio: '',
        joinedAt: new Date().toISOString(),
        role: 'member',
        canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true,
      });
      await updateDoc(doc(db, 'families', familyId, 'invitations', invitationId), {
        status: 'accepted',
        acceptedAt: new Date().toISOString(),
        acceptedBy: currentUser.uid,
      });
      onNext(familyName);
    } catch (err) {
      setError('Failed to accept invitation. Please try again.');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-12 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cerulean" />
        <p className="text-gray-500 text-sm">Loading your invitation…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-center py-4">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-gray-600 text-sm">{error}</p>
        <button onClick={() => onNext()} className="w-full py-2 text-sm text-cerulean hover:text-cerulean/80 transition-colors">
          Continue without joining
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-space-cadet">You're Invited!</h2>
        <p className="text-gray-500 mt-1 text-sm">
          {invitation?.invitedByName} has invited you to join their family Bible.
        </p>
      </div>

      <div className="bg-cerulean/5 border border-cerulean/20 rounded-2xl p-5 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Family</span>
          <span className="font-semibold text-space-cadet">{familyName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Invited by</span>
          <span className="font-semibold text-space-cadet">{invitation?.invitedByName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Sent to</span>
          <span className="font-semibold text-space-cadet">{invitation?.invitedUserEmail}</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      <button
        onClick={handleAccept}
        disabled={accepting}
        className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {accepting ? (
          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Joining…</span></>
        ) : (
          <><span>Accept & Join {familyName}</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg></>
        )}
      </button>
    </div>
  );
}

// ── Step 2 (creator only): Invite Members ─────────────────────────────────────

function InviteStep({ familyId, familyName, onNext }) {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) { setError('Enter a valid email address.'); return; }
    if (sent.includes(trimmed)) { setError('Already invited that address.'); return; }

    // Enforce the member limit at send time. A new family's plan mirrors its
    // creator's, so Friends Access (the beta plan) grants unlimited members.
    // Seats reserved so far: the creator (1) + invites already sent this session.
    const { memberCount: memberLimit } = getLimits(getUserPlan(currentUser));
    if (1 + sent.length + 1 > memberLimit) {
      setError(`Your plan includes up to ${memberLimit} family members. Upgrade at lineagebible.com to invite more.`);
      return;
    }

    setLoading(true);
    setError('');
    try {
      await addDoc(collection(db, 'families', familyId, 'invitations'), {
        invitedUserEmail: trimmed,
        invitedUserId: null,
        familyId,
        familyName,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      setSent(prev => [...prev, trimmed]);
      setEmail('');
    } catch (err) {
      setError('Failed to send invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-14 h-14 bg-cerulean/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <svg className="w-7 h-7 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-space-cadet">Invite Your Family</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Send email invitations to <strong>{familyName}</strong>. You can always do this later from the Family tab.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="family@example.com"
          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors text-sm"
          autoFocus
        />
        <button
          onClick={handleSend}
          disabled={loading || !email.trim()}
          className="bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold px-4 py-3 rounded-xl hover:opacity-90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '…' : 'Send'}
        </button>
      </div>

      {sent.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-1">
          <p className="text-xs font-semibold text-green-700 mb-2">Invitations sent:</p>
          {sent.map(e => (
            <div key={e} className="flex items-center gap-2 text-sm text-green-700">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              {e}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onNext}
        className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg"
      >
        Continue
      </button>
      <button
        onClick={onNext}
        className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// ── Step 3: Profile ───────────────────────────────────────────────────────────

function ProfileStep({ onNext, familyName }) {
  const { currentUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [notifNewRecordings, setNotifNewRecordings] = useState(true);
  const [notifStudyNotes, setNotifStudyNotes] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser?.displayName) {
      const parts = currentUser.displayName.split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    }
  }, [currentUser]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || currentUser.email;
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName,
        phoneNumber: phoneNumber.trim(),
        ...(profileImageUrl ? { photoURL: profileImageUrl } : {}),
        notificationPreferences: {
          newRecordings: notifNewRecordings,
          studyNotes: notifStudyNotes,
          frequency: 'daily',
        },
      });
      onNext();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {familyName ? (
        <div className="text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-space-cadet">You've Joined!</h2>
          <p className="text-gray-500 mt-1 text-sm">Welcome to <strong>{familyName}</strong>. Now let's set up your profile.</p>
        </div>
      ) : (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-space-cadet">Your Profile</h2>
          <p className="text-gray-500 mt-1 text-sm">Help your family know who you are.</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      <div className="flex flex-col items-center gap-2">
        <ImageUpload
          currentImage={profileImageUrl}
          onUploadSuccess={(url) => setProfileImageUrl(url)}
          userId={currentUser?.uid}
          size="w-28 h-28"
        />
        <p className="text-xs text-gray-400">Tap to upload a photo</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-space-cadet mb-1">First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="Jane"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-space-cadet mb-1">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder="Smith"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-space-cadet mb-1">
          Phone Number <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value)}
          placeholder="+1 (555) 000-0000"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-colors text-sm"
        />
      </div>

      <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
        <p className="text-sm font-semibold text-space-cadet">Notification Preferences</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">New Recordings</p>
            <p className="text-xs text-gray-500">When a family member records a passage</p>
          </div>
          <Toggle checked={notifNewRecordings} onChange={setNotifNewRecordings} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Study Notes</p>
            <p className="text-xs text-gray-500">When someone shares a study note</p>
          </div>
          <Toggle checked={notifStudyNotes} onChange={setNotifStudyNotes} />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Saving…' : 'Save & Continue'}
      </button>
      <button onClick={onNext} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
        Skip for now
      </button>
    </div>
  );
}

// ── Step 4: Video + App Download ──────────────────────────────────────────────

const APP_STORE_URL = null;   // update when live: 'https://apps.apple.com/app/...'
const PLAY_STORE_URL = null;  // update when live: 'https://play.google.com/store/apps/...'

function VideoStep({ onFinish }) {
  const [tapped, setTapped] = useState(false);

  const isIOS     = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-space-cadet">Welcome to Lineage Bible</h2>
        <p className="text-gray-500 mt-1 text-sm">Watch this short video to get the most out of the app.</p>
      </div>

      <div
        className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-br from-space-cadet to-cerulean cursor-pointer group"
        style={{ paddingBottom: '56.25%' }}
        onClick={() => setTapped(true)}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-4">
          {!tapped ? (
            <>
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-all">
                <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <div className="text-center px-4">
                <p className="font-semibold text-lg">Getting Started</p>
                <p className="text-sm text-white/70">Lineage Bible · 2 min</p>
              </div>
              <div className="absolute bottom-3 right-3 bg-black/30 rounded-md px-2 py-1 text-xs text-white/80">
                Video coming soon
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl px-6 py-4 text-center shadow-2xl">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 text-sm">You're all set!</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-cerulean/5 border border-cerulean/20 rounded-2xl p-4 space-y-2">
        <p className="text-sm font-semibold text-space-cadet">Quick Tips</p>
        <ul className="space-y-1.5 text-sm text-gray-600">
          <li className="flex items-start gap-2"><span className="text-cerulean mt-0.5">•</span><span>Go to <strong>Record</strong> to read a Bible passage aloud for your family</span></li>
          <li className="flex items-start gap-2"><span className="text-cerulean mt-0.5">•</span><span>Use <strong>Listen</strong> to hear your family's recordings</span></li>
          <li className="flex items-start gap-2"><span className="text-cerulean mt-0.5">•</span><span>Visit <strong>Family</strong> to invite members and see activity</span></li>
          <li className="flex items-start gap-2"><span className="text-cerulean mt-0.5">•</span><span>Earn <strong>badges</strong> as you record more of the Bible</span></li>
        </ul>
      </div>

      {/* App download — show the relevant platform button, or both on desktop */}
      {(APP_STORE_URL || PLAY_STORE_URL || true) && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-space-cadet mb-3 text-center">Get the App</p>
          <div className={`flex gap-3 ${(!isIOS && !isAndroid) ? 'flex-col sm:flex-row' : 'flex-col'}`}>
            {(!isAndroid || isIOS || (!isIOS && !isAndroid)) && (
              <a
                href={APP_STORE_URL || '#'}
                onClick={e => { if (!APP_STORE_URL) e.preventDefault(); }}
                className={`flex items-center justify-center gap-2 flex-1 py-2.5 px-4 rounded-xl border-2 transition-all ${
                  APP_STORE_URL
                    ? 'border-space-cadet bg-space-cadet text-white hover:bg-space-cadet/90'
                    : 'border-gray-300 bg-white text-gray-400 cursor-default'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs leading-none opacity-70">{APP_STORE_URL ? 'Download on the' : 'Coming soon —'}</div>
                  <div className="text-sm font-semibold leading-tight">App Store</div>
                </div>
              </a>
            )}
            {(!isIOS || isAndroid || (!isIOS && !isAndroid)) && (
              <a
                href={PLAY_STORE_URL || '#'}
                onClick={e => { if (!PLAY_STORE_URL) e.preventDefault(); }}
                className={`flex items-center justify-center gap-2 flex-1 py-2.5 px-4 rounded-xl border-2 transition-all ${
                  PLAY_STORE_URL
                    ? 'border-space-cadet bg-space-cadet text-white hover:bg-space-cadet/90'
                    : 'border-gray-300 bg-white text-gray-400 cursor-default'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.18 23.76c.3.17.64.24.99.19l12.6-7.27-2.75-2.75-10.84 9.83zm-1.67-20.5C1.19 3.69 1 4.2 1 4.75v14.5c0 .56.19 1.06.51 1.49l.08.08 8.12-8.12v-.19L1.59 4.38l-.08.88zm17.63 9.06L16.3 10l2.84-2.32-2.84-7.73L3.69 7.23l8.08 8.08 7.37-3zM20.12 22l-6.69-3.86 2.75-2.75 6.69 3.86c.69.4.69 1.42 0 1.82l-.34.2c-.68.39-1.53.12-2.41-.27z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs leading-none opacity-70">{PLAY_STORE_URL ? 'Get it on' : 'Coming soon —'}</div>
                  <div className="text-sm font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            )}
          </div>
        </div>
      )}

      <button
        onClick={onFinish}
        className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg flex items-center justify-center gap-2"
      >
        <span>Go to Dashboard</span>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
    </div>
  );
}

// ── Main Shell ────────────────────────────────────────────────────────────────

function OnboardUser() {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const intent            = searchParams.get('intent') || 'create';
  const startStep         = searchParams.get('start') || null;
  const initialFamilyName = searchParams.get('familyName') || '';
  const linkFamilyId      = searchParams.get('familyId') || null;
  const linkInvitationId  = searchParams.get('invitationId') || null;

  const hasLinkInvite  = !!(linkFamilyId && linkInvitationId);
  const isInvitedFlow  = startStep === 'profile'; // legacy: already accepted, skip to profile

  const [flow, setFlow] = useState(
    hasLinkInvite   ? 'link-invite'
    : isInvitedFlow ? 'invited'
    : intent === 'join' ? 'join'
    : 'create'
  );

  const activeSteps = flow === 'link-invite' ? STEPS_LINK_INVITE
    : flow === 'invited'  ? STEPS_INVITED
    : flow === 'join'     ? STEPS_JOIN
    : STEPS_CREATE;

  const [step, setStep] = useState(
    hasLinkInvite   ? 'accept-invite'
    : isInvitedFlow ? 'profile'
    : intent === 'join' ? 'join'
    : 'create'
  );
  const [checking, setChecking] = useState(true);
  const [createdFamilyId, setCreatedFamilyId] = useState(null);
  const [createdFamilyName, setCreatedFamilyName] = useState('');
  const [joinedFamilyName, setJoinedFamilyName] = useState(initialFamilyName);

  useEffect(() => {
    if (!currentUser) { navigate('/auth'); return; }
    getDoc(doc(db, 'users', currentUser.uid)).then(async snap => {
      const data = snap.exists() ? snap.data() : {};

      const hasFamilies = data.familyIds && data.familyIds.length > 0;
      if (data.onboardingCompleted && hasFamilies && !isInvitedFlow) {
        navigate('/dashboard');
        return;
      }

      // Already submitted a join request — send them to the awaiting screen
      if (data.pendingFamilyId && !hasFamilies) {
        navigate('/awaiting-approval');
        return;
      }

      // Fallback: check for a pending request even if pendingFamilyId wasn't stored
      // (covers requests submitted before this field existed)
      if (!hasFamilies && data.onboardingCompleted) {
        try {
          const reqSnap = await getDocs(query(
            collectionGroup(db, 'requests'),
            where('userId', '==', currentUser.uid),
            where('status', '==', 'pending')
          ));
          if (!reqSnap.empty) {
            const reqDoc = reqSnap.docs[0];
            const pendingFamilyId = reqDoc.ref.parent.parent.id;
            let pendingFamilyName = '';
            try {
              const famSnap = await getDoc(doc(db, 'families', pendingFamilyId));
              pendingFamilyName = famSnap.data()?.name || '';
            } catch (_) {}
            // Backfill so future loads skip this query
            updateDoc(doc(db, 'users', currentUser.uid), { pendingFamilyId, pendingFamilyName }).catch(() => {});
            navigate('/awaiting-approval');
            return;
          }
        } catch (_) {}
      }

      // Persist intent so Dashboard can resume them correctly after logout
      if (!data.signupIntent && intent && !isInvitedFlow) {
        updateDoc(doc(db, 'users', currentUser.uid), { signupIntent: intent }).catch(() => {});
      }

      // If they already have a family (created one before logging out), skip family step
      if (hasFamilies && (step === 'create' || step === 'join')) {
        // Try to show the right family name on the profile welcome screen
        try {
          const famSnap = await getDoc(doc(db, 'families', data.familyIds[0]));
          if (famSnap.exists()) setJoinedFamilyName(famSnap.data().name || '');
        } catch (_) {}
        setStep('profile');
      }

      setChecking(false);
    }).catch(() => setChecking(false));
  }, [currentUser, navigate, isInvitedFlow]);

  const advance = () => {
    const idx = activeSteps.indexOf(step);
    if (idx < activeSteps.length - 1) setStep(activeSteps[idx + 1]);
  };

  const goBack = () => {
    const idx = activeSteps.indexOf(step);
    if (idx > 0) setStep(activeSteps[idx - 1]);
  };

  const canGoBack = activeSteps.indexOf(step) > 0;

  const markComplete = async () => {
    try {
      const snap = await import('firebase/firestore').then(({ getDoc, doc: d }) => getDoc(d(db, 'users', currentUser.uid)));
      const data = snap.exists() ? snap.data() : {};
      await updateDoc(doc(db, 'users', currentUser.uid), { onboardingCompleted: true });
      // AuthContext's currentUser is a one-shot snapshot from sign-in; the family
      // just created/joined during onboarding isn't on it yet. Refresh before
      // leaving, or Dashboard's guard reads stale (family-less) state and bounces
      // straight back to /onboard — the "Go to Dashboard does nothing" bug.
      await refreshUserData();
      if (data.pendingFamilyId && !(data.familyIds?.length > 0)) {
        navigate('/awaiting-approval');
        return;
      }
    } catch (err) {
      console.error('Could not mark onboarding complete:', err);
    }
    navigate('/dashboard');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cerulean" />
      </div>
    );
  }

  // Profile step needs the right name depending on the path
  const profileFamilyName = isInvitedFlow ? initialFamilyName : joinedFamilyName;

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-start sm:items-center justify-center pt-8 pb-16 sm:py-8 px-4 overflow-y-auto">
      <div
        className="fixed inset-0 z-0 bg-no-repeat bg-cover bg-center opacity-10"
        style={{ backgroundImage: "url('/images/family-bible-app2.jpg')", filter: 'grayscale(100%)' }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            {canGoBack ? (
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-space-cadet transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <div />
            )}
            <div className="w-10 h-10 bg-gradient-to-br from-cerulean to-aqua-deep rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="w-16" />
          </div>

          <ProgressBar currentStep={step} steps={activeSteps} />

          {step === 'accept-invite' && (
            <AcceptInviteStep
              familyId={linkFamilyId}
              invitationId={linkInvitationId}
              onNext={(fName) => {
                if (fName) setJoinedFamilyName(fName);
                advance();
              }}
            />
          )}
          {step === 'create' && (
            <CreateFamilyStep
              onNext={(fId, fName) => {
                setCreatedFamilyId(fId);
                setCreatedFamilyName(fName);
                advance();
              }}
              onSwitchToJoin={() => { setFlow('join'); setStep('join'); }}
            />
          )}
          {step === 'invite' && (
            <InviteStep
              familyId={createdFamilyId}
              familyName={createdFamilyName}
              onNext={advance}
            />
          )}
          {step === 'join' && (
            <JoinFamilyStep
              onNext={(fName) => {
                if (fName) setJoinedFamilyName(fName);
                advance();
              }}
            />
          )}
          {step === 'profile' && (
            <ProfileStep onNext={advance} familyName={profileFamilyName} />
          )}
          {step === 'video' && (
            <VideoStep onFinish={markComplete} />
          )}
        </div>

        <p className="text-center text-white/50 text-xs mt-6">
          Step {activeSteps.indexOf(step) + 1} of {activeSteps.length}
        </p>
      </div>
    </div>
  );
}

export default OnboardUser;
